import { createBrowserRouter } from 'react-router-dom'
import { AuthLayout } from '@/features/auth/components/AuthLayout'
import { SignIn } from '@/features/auth/components/SignIn'
import { SignUp } from '@/features/auth/components/SignUp'
import { ForgotPassword } from '@/features/auth/components/ForgotPassword'
import { ResetPassword } from '@/features/auth/components/ResetPassword'
import { AuthCallback } from '@/features/auth/components/AuthCallback'
import { PrivateLayout } from '@/components/common/layout/PrivateLayout'
import { ProtectedRoute } from '@/components/common/navigation/ProtectedRoute'
import { Dashboard } from '@/features/dashboard/pages/Dashboard'
import { SignOut } from '@/features/auth/components/SignOut'
import { ArtworkDetailPage } from '@/features/artworks/pages/ArtworkDetailPage'
import { UploadPage } from '@/features/artworks/pages/UploadPage'
import { SalesPage } from '@/features/sales/pages/SalesPage'
import { ExportSalesPage } from '@/features/sales/pages/ExportSalesPage'
import { ExportArtworksPage } from '@/features/artworks/pages/ExportArtworksPage'
import { ExportArtistsPage } from '@/features/users/pages/ExportArtistsPage'
import { InvoicesPage } from '@/features/invoices/pages/InvoicesPage'
import { FulfillmentPage, FulfillmentDetailPage } from '@/features/fulfillment'
import { ProfilePage } from '@/features/users/pages/ProfilePage'
import { UserAdminPage } from '@/features/users/pages/UserAdminPage'
import { FeatureFlagsPage } from '@/features/flags/pages/FeatureFlagsPage'
import { FlagRoute } from '@/features/flags'
import { TaskQueuePage } from '@/features/tasks/pages/TaskQueuePage'
import { FilmStudioPage } from '@/features/films/FilmStudioPage'
import { WorldStudioPage } from '@/features/worlds/WorldStudioPage'
import { FilmSocialPanel } from '@/features/films/FilmSocialPanel'
import { ContentStudioPage, LegacyStudioRoute } from '@/features/content-studio/ContentStudioPage'
import { ArtworksPage } from '@/features/admin/pages/ArtworksPage'
import { ArtistAdminPage, ArtistDetailPage } from '@/features/artists/pages'
// Easel Portfolio Pages
import {
  EaselDashboard,
  HeroStudioPage,
  CollectionsPage,
  CollectionDetailPage,
  JournalPage,
  JournalPostPage,
  ShopPage,
  ContactPage,
  CommissionsPage,
  CommissionDetailPage,
  SettingsPage,
  ShortLinksPage,
  SiteContentPage,
  ProfilePage as EaselProfilePage,
  AppearancePage,
} from '@/features/easel/pages'
import {
  AnalyticsPage,
  SocialCreatorPage,
  SocialCalendarPage,
  EmailMarketingPage,
  DropsPage,
} from '@/features/studio/pages'
import { SupportPage, TicketDetailPage } from '@/features/support'
import { InboxPage } from '@/features/inbox'
import { ProspectsPage, ProspectDetailPage, ClaimsPage } from '@/features/prospects'
import { ExternalCatalogPage, ExternalArtistPage } from '@/features/external'
import { PicassoConsolePage, PicassoSessionsPage, ShopperSearchesPage } from '@/features/picasso'
import { KnowledgePage, ConceptsPage, OperationsPage, DomainsPage, ConceptPage, ReviewPage, SearchPreviewPage, ExplorePage, ChangesPage, RulesPage, ProposalsPage, ProgressPage, OpportunitiesPage, AgentPage, GraphExplorerPage } from '@/features/knowledge'
import { OverviewPage as GrowthOverviewPage, CampaignPage as GrowthCampaignPage, SearchTermsPage as GrowthSearchTermsPage, ProductsPage as GrowthProductsPage, StrategyPage as GrowthStrategyPage } from '@/features/growth'
import { DestinationsPage as FeedDestinationsPage, ItemsPage as FeedItemsPage, RoomsSetsPage as FeedRoomsSetsPage, ProposalsPage as FeedProposalsPage } from '@/features/feeds'
import { ContentQueuePage, ContentItemPage, RecommendationsCoveragePage, ContentCoveragePage } from '@/features/content'
import { BiographyPage } from '@/features/easel/pages/BiographyPage'

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AuthLayout />,
    children: [
      { index: true, element: <SignIn /> },
      { path: 'signin', element: <SignIn /> },
      { path: 'signup', element: <SignUp /> },
      { path: 'forgot-password', element: <ForgotPassword /> },
      { path: 'reset', element: <ResetPassword /> },
      // Where a provider sign-in returns. Public: the visitor is not authenticated in this app's
      // terms until this route exchanges the session for a platform user.
      { path: 'auth/callback', element: <AuthCallback /> },
    ],
  },
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <PrivateLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Dashboard /> },
      { path: 'dashboard', element: <Dashboard /> },
      // Artwork routes (shows different view based on user role)
      { path: 'artworks', element: <ArtworksPage /> },
      { path: 'artworks/:artworkId', element: <ArtworkDetailPage /> },
      { path: 'upload', element: <UploadPage /> },
      // Sales routes
      { path: 'sales', element: <SalesPage /> },
      // Profile routes
      { path: 'account', element: <ProfilePage /> },
      { path: 'profile', element: <ProfilePage /> },
      // Manager routes
      { path: 'support', element: <SupportPage /> },
      { path: 'support/:ticketId', element: <TicketDetailPage /> },
      { path: 'prospects', element: <ProspectsPage /> },
      { path: 'prospects/claims', element: <ClaimsPage /> },
      { path: 'prospects/:prospectId', element: <ProspectDetailPage /> },
      { path: 'external', element: <ExternalCatalogPage /> },
      { path: 'external/:provider/artists/:externalId', element: <ExternalArtistPage /> },
      { path: 'picasso', element: <PicassoConsolePage /> },
      { path: 'picasso/sessions', element: <PicassoSessionsPage /> },
      { path: 'picasso/s/:sessionId', element: <PicassoConsolePage /> },
      { path: 'picasso/shopper', element: <ShopperSearchesPage /> },
      { path: 'knowledge', element: <KnowledgePage /> },
      { path: 'knowledge/concepts', element: <ConceptsPage /> },
      { path: 'knowledge/operations', element: <OperationsPage /> },
      { path: 'knowledge/domains', element: <DomainsPage /> },
      { path: 'knowledge/graph', element: <GraphExplorerPage /> },
      { path: 'knowledge/agent', element: <AgentPage /> },
      { path: 'knowledge/proposals', element: <ProposalsPage /> },
      { path: 'knowledge/progress', element: <ProgressPage /> },
      { path: 'knowledge/opportunities', element: <OpportunitiesPage /> },
      { path: 'knowledge/review', element: <ReviewPage /> },
      { path: 'knowledge/search', element: <SearchPreviewPage /> },
      { path: 'knowledge/explore', element: <ExplorePage /> },
      { path: 'knowledge/changes', element: <ChangesPage /> },
      { path: 'knowledge/rules', element: <RulesPage /> },
      { path: 'knowledge/:ucid', element: <ConceptPage /> },
      { path: 'growth', element: <GrowthOverviewPage /> },
      { path: 'growth/campaigns/:campaignId', element: <GrowthCampaignPage /> },
      { path: 'growth/search-terms', element: <GrowthSearchTermsPage /> },
      { path: 'growth/products', element: <GrowthProductsPage /> },
      { path: 'growth/strategy', element: <GrowthStrategyPage /> },
      { path: 'feeds', element: <FeedDestinationsPage /> },
      { path: 'feeds/items', element: <FeedItemsPage /> },
      { path: 'feeds/rooms', element: <FeedRoomsSetsPage /> },
      { path: 'feeds/proposals', element: <FeedProposalsPage /> },
      { path: 'content', element: <ContentQueuePage /> },
      { path: 'content/recommendations', element: <RecommendationsCoveragePage /> },
      { path: 'content/coverage', element: <ContentCoveragePage /> },
      { path: 'content/:id', element: <ContentItemPage /> },
      { path: 'tasks', element: <TaskQueuePage /> },
      // Manager tools first; artists only once the flag is rolled out (`off` today). The sidebar
      // row and the route are gated by the same flag so the page is not one typed URL away.
      { path: 'content-studio', element: <ContentStudioPage /> },
      { path: 'films', element: <FlagRoute flag="social.film_studio"><LegacyStudioRoute from="films"><FilmStudioPage /></LegacyStudioRoute></FlagRoute> },
      { path: 'worlds', element: <FlagRoute flag="social.world_studio"><LegacyStudioRoute from="worlds"><WorldStudioPage /></LegacyStudioRoute></FlagRoute> },
      {
        path: 'films/accounts',
        element: (
          <FlagRoute flag="social.film_studio">
            <LegacyStudioRoute from="accounts"><div className="mx-auto max-w-4xl p-4"><FilmSocialPanel /></div></LegacyStudioRoute>
          </FlagRoute>
        ),
      },
      { path: 'users', element: <UserAdminPage /> },
      { path: 'releases', element: <FeatureFlagsPage /> },
      { path: 'artists', element: <ArtistAdminPage /> },
      // Code-split: the activity charts load only when a manager opens the report.
      { path: 'artist-activity', lazy: async () => ({ Component: (await import('@/features/activity/pages/ArtistActivityPage')).ArtistActivityPage }) },
      { path: 'artists/:artistId', element: <ArtistDetailPage /> },
      { path: 'export-artworks', element: <ExportArtworksPage /> },
      { path: 'export-sales', element: <ExportSalesPage /> },
      { path: 'export-artists', element: <ExportArtistsPage /> },
      { path: 'invoices', element: <InvoicesPage /> },
      { path: 'fulfillment', element: <FulfillmentPage /> },
      { path: 'fulfillment/:id', element: <FulfillmentDetailPage /> },
      // Studio/Easel Portfolio routes
      { path: 'easel', element: <EaselDashboard /> },
      { path: 'easel/hero', element: <HeroStudioPage /> },
      { path: 'easel/collections', element: <CollectionsPage /> },
      { path: 'easel/collections/:id', element: <CollectionDetailPage /> },
      { path: 'easel/biography', element: <BiographyPage /> },
      { path: 'easel/journal', element: <JournalPage /> },
      { path: 'easel/journal/:id', element: <JournalPostPage /> },
      { path: 'easel/shop', element: <ShopPage /> },
      { path: 'easel/contact', element: <ContactPage /> },
      { path: 'easel/inbox', element: <InboxPage /> },
      { path: 'easel/commissions', element: <CommissionsPage /> },
      { path: 'easel/commissions/:id', element: <CommissionDetailPage /> },
      { path: 'easel/settings', element: <SettingsPage /> },
      { path: 'easel/site-content', element: <SiteContentPage /> },
      { path: 'easel/profile', element: <EaselProfilePage /> },
      { path: 'easel/appearance', element: <AppearancePage /> },
      { path: 'easel/short-links', element: <ShortLinksPage /> },
      // New Studio routes
      { path: 'analytics', element: <AnalyticsPage /> },
      { path: 'social/creator', element: <SocialCreatorPage /> },
      { path: 'social/calendar', element: <SocialCalendarPage /> },
      { path: 'marketing/email', element: <EmailMarketingPage /> },
      { path: 'marketing/drops', element: <DropsPage /> },
      // Auth
      { path: 'signout', element: <SignOut /> },
    ],
  },
])
